import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import EntityMap from './EntityMap';
import Textfit from 'react-textfit';
import LinkPreview from './LinkPreview';
import QuotedTweet from './QuotedTweet';
import { PostTextProps } from './types';
import { useClickActionContext } from 'components/Widget/context/ClickActionContext';
import { LINE_CLAMP_STYLES } from '@sprinklr/display-builder/constants';
import $ from 'jquery';
import './PostText.scss';

(window as any).jQuery = $; // HACKTOWN:  Text Tailor plugin is expecting window.jQuery defined, but it's not because of jQuery CommonJS implementation - dboyd

function PostText({
    text = '',
    textFormat = 'fit',
    clamp = 2,
    classes = '',
    blastDelimiter = 'letters',
    blastText = true,
    maxCharacters = 180,
    isComment = false,
    channel,
    rtl,
    language,
    textEntities,
    showUrls,
    clickableUrls,
    showPostTextOverlay,
    showPostTextOverlayAsGradient,
    activePostsInWindow,
    postOffset,
    animationDelay,
    animationLength,
    inGroup,
    textFitMin,
    classOverrides,
    postVersion2,
    lineClampVerticalAlign,
    isReview,
    emoji,
    showLinkPreview,
    urlEntities,
    linkPreviewSize,
    showQuotedTweet,
    quotedTweet,
    quotedTweetSize,
    postLayoutOrientation,
    textSize,
    teleprompterSpeed,
}: PostTextProps) {
    const ref = useRef(null);
    const { onEntityClick } = useClickActionContext();
    const [teleprompterState, setTeleprompterState] = useState({
        offset: 0,
        duration: 0,
    });

    useEffect(() => {
        if (ref.current && teleprompterState.offset === 0) {
            if (ref.current.scrollHeight > ref.current.offsetHeight) {
                const offset = ref.current.scrollHeight - ref.current.offsetHeight + textSize * 1.5;
                const duration = offset / (Math.max(10, teleprompterSpeed) * 0.004);
                setTeleprompterState({
                    offset: offset * -1,
                    duration,
                });
            }
        }
    }, [teleprompterSpeed, teleprompterState.offset, textSize]);

    const resolvedMaxCharacters = textFormat === 'teleprompter' ? -1 : maxCharacters;

    const textContainerClasses = classNames(
        `post_text_container rel flex vertical ${classOverrides} format_${textFormat} w-100`,
        {
            [lineClampVerticalAlign || 'middle']: textFormat === 'clamp',
            [classes]: !postVersion2,
        }
    );

    const textClasses = classNames(`post_text rel format_${textFormat}`, {
        [`clamp-${clamp}`]: textFormat === 'clamp' ? true : false,
        'flex rel middle': textFormat === 'fit' ? true : false,
        center: textFormat === 'fit' && !isComment ? true : false,
        left: textFormat === 'fit' && isComment ? true : false,
        'flex post_text-comment': isComment,
        post_text_review: isReview,
    });

    const resolvedText = useMemo(() => {
        let tempText = text ?? '';
        // limit length / add ellipsis
        if (
            textFormat !== 'teleprompter' &&
            resolvedMaxCharacters !== -1 &&
            tempText &&
            tempText.length > resolvedMaxCharacters
        ) {
            tempText = tempText.slice(0, resolvedMaxCharacters) + '...';
        } else if (tempText.length > 3000) {
            // I don't care who you are, 3000 characters is too much
            tempText = tempText.slice(0, 3000) + '...';
        }
        return tempText;
    }, [resolvedMaxCharacters, text, textFormat]);

    const handleEntityClick = useCallback(
        (event, fragment) => {
            if (onEntityClick) {
                onEntityClick({ event, url: fragment.url });
            }
        },
        [onEntityClick]
    );

    const textOverlay = useMemo(
        () =>
            showPostTextOverlay && !showPostTextOverlayAsGradient ? (
                <div
                    className={'post_text_overlay fullbleed'}
                    data-testid='post-text-overlay'></div>
            ) : null,
        [showPostTextOverlay, showPostTextOverlayAsGradient]
    );

    const linkPreview = useMemo(
        () =>
            !showLinkPreview ? null : (
                <LinkPreview urlEntity={urlEntities.message[0]} size={linkPreviewSize} />
            ),
        [showLinkPreview, urlEntities, linkPreviewSize]
    );

    const quotedTweetView = useMemo(
        () =>
            !showQuotedTweet ? null : (
                <QuotedTweet
                    quotedTweet={quotedTweet}
                    quotedTweetSize={quotedTweetSize}
                    postLayoutOrientation={postLayoutOrientation}
                />
            ),
        [showQuotedTweet, quotedTweet, quotedTweetSize, postLayoutOrientation]
    );

    switch (textFormat) {
        case 'fit':
            return (
                <div className={textContainerClasses} data-testid='post-text-fit'>
                    {textOverlay}

                    <Textfit className={textClasses} max={400} min={textFitMin}>
                        <EntityMap
                            rawText={resolvedText}
                            language={language}
                            rtl={rtl}
                            maxCharacters={resolvedMaxCharacters}
                            emoji={emoji}
                            entityMap={textEntities && textEntities.message}
                            blastDelimiter={blastDelimiter}
                            showUrls={showUrls}
                            clickableUrls={clickableUrls}
                            blastText={blastText}
                            channel={channel}
                            onEntityClick={handleEntityClick}
                        />
                    </Textfit>

                    {linkPreview}
                    {quotedTweetView}
                </div>
            );
        case 'clamp':
            return (
                <div className={textContainerClasses} data-testid='post-text-clamp'>
                    {textOverlay}

                    <EntityMap
                        rawText={resolvedText}
                        language={language}
                        rtl={rtl}
                        maxCharacters={resolvedMaxCharacters}
                        emoji={emoji}
                        entityMap={textEntities && textEntities.message}
                        blastDelimiter={blastDelimiter}
                        blastText={blastText}
                        styles={LINE_CLAMP_STYLES}
                        classes={textClasses}
                        showUrls={showUrls}
                        clickableUrls={clickableUrls}
                        clamp={clamp}
                        channel={channel}
                        onEntityClick={handleEntityClick}
                    />

                    {linkPreview}
                    {quotedTweetView}
                </div>
            );
        case 'teleprompter':
            const case1 =
                !inGroup &&
                activePostsInWindow === 3 &&
                (postOffset === 4 || postOffset === 3 || postOffset === 2 || postOffset === 1);
            const case2 =
                !inGroup && activePostsInWindow === 1 && (postOffset === 2 || postOffset === 1);
            const case3 = inGroup && (activePostsInWindow === 1 || activePostsInWindow === 0);

            const teleprompterTextContainerClasses = textContainerClasses;
            let teleprompterStyles: any = {};

            if ((case1 || case2 || case3) && teleprompterState.offset !== 0) {
                teleprompterStyles = {
                    transform: 'translate3d(0,' + teleprompterState.offset + 'px,0)',
                    transitionDuration: teleprompterState.duration + 'ms',
                    transitionDelay: animationDelay + animationLength + 'ms',
                    transitionProperty: 'transform',
                };
            }

            return (
                <div
                    className={teleprompterTextContainerClasses}
                    ref={ref}
                    data-testid='post-text-teleprompter'>
                    {textOverlay}
                    <EntityMap
                        rawText={resolvedText}
                        language={language}
                        rtl={rtl}
                        maxCharacters={5000}
                        entityMap={textEntities && textEntities.message}
                        blastDelimiter={blastDelimiter}
                        blastText={false}
                        styles={teleprompterStyles}
                        classes={textClasses}
                        showUrls={showUrls}
                        clickableUrls={clickableUrls}
                        clamp={clamp}
                        onEntityClick={handleEntityClick}
                    />
                    {linkPreview}
                    {quotedTweetView}
                </div>
            );
        default:
            return (
                <div className={textContainerClasses} data-testid='post-text'>
                    {textOverlay}

                    <EntityMap
                        rawText={resolvedText}
                        language={language}
                        rtl={rtl}
                        maxCharacters={resolvedMaxCharacters}
                        emoji={emoji}
                        entityMap={textEntities && textEntities.message}
                        classes={textClasses}
                        styles={undefined}
                        blastDelimiter={blastDelimiter}
                        showUrls={showUrls}
                        clickableUrls={clickableUrls}
                        blastText={blastText}
                        channel={channel}
                        onEntityClick={handleEntityClick}
                    />
                    {linkPreview}
                    {quotedTweetView}
                </div>
            );
    }
}

export default observer(PostText);
